import { Component } from '@angular/core';
import { SignalRService } from './services/signal-r/signal-r.service';
import { CookieService } from 'ngx-cookie-service';
import { AuthenticationService } from './services/authentication/authentication.service';
import { AlertService } from './services/alert/alert.service';
import { Store } from '@ngxs/store';
import { SetCurrentUser } from './store/actions';
import { InitStoreAndListenersService } from './services/signal-r/init-store-listeners.service';
import { environment } from 'src/environments/environment';
import { RedirectService } from './components/redirect/redirect.service';
import { Router } from '@angular/router';
import { LoadingService } from './services/loading/loading.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent {
  title = 'ASCC-FE4.0';
  expired = false;

  constructor(
    private signalrService: SignalRService,
    private cookieService: CookieService,
    private authService: AuthenticationService,
    private alert: AlertService,
    private initService: InitStoreAndListenersService,
    private store: Store,
    private redirect: RedirectService,
    private router: Router,
    public loadingService: LoadingService,
  ) {
    window.addEventListener('mousedown', this.updateMousePosition.bind(this));
  }

  ngOnInit() {
    this.signalrService.startConnection();
    this.initService.initListeners();
    this.disableTitleTooltip();

    this.authService.getCookieValue().subscribe(data => {
      this.expired = !data;
      this.login();
    });
  }

  /**
   * Hides the workflow assistant while the user is on the unauthenticated login route.
   */
  get showWorkflowAssistant(): boolean {
    // Keep the assistant out of the login screen before ASCC user context is available.
    return !this.router.url.split('?')[0].startsWith('/login');
  }

  private updateMousePosition(event: MouseEvent) {
    this.redirect.mouseX = event.clientX + 'px';
    this.redirect.mouseY = event.clientY + 'px';
  }

  async login() {
    if (document.location.hostname === environment.productionEnvironment) {
      const cookieValue = this.cookieService.get('_shibobj');
      if (cookieValue) {
        let userEmail: string;
        try {
          userEmail = JSON.parse(cookieValue).email;
        } catch (e) {
          console.error('Failed to parse _shibobj cookie', e);
          this.authService.logout();
          this.router.navigate(['login']);
          return;
        }
        try {
          const response = await firstValueFrom(this.authService.loginMFA(userEmail));
          if (response?.code == 202) {
            console.log("User has been Authenticated from MFA");
            this.navigateOrRedirect();
          } else {
            this.alert.error("User Not Found or Not Authorized, Please contact Control Room!", "Unauthorized!");
            console.log("User not found");
            this.authService.logout();
            this.router.navigate(['login']);
          }
        } catch (error: any) {
          error.status === 423 ? this.alert.message('Login_UserInactive') : error.status === 401 ? this.alert.message('Login_UserUndefined') : this.alert.defaultError();
          console.log(error);
          this.authService.logout();
        } finally {
          this.initService.initStore();
        }
      }
      else {
        this.alert.warning(
          "Your session has expired. For security purposes, please log in again.", "Session Expired", -1);
        this.authService.logout();
        this.router.navigate(['login']);
      }

    } else {
      this.store.dispatch(new SetCurrentUser(localStorage.getItem('Token')));
      this.initService.initStore();
    }
  }

  navigateOrRedirect() {
    const route = localStorage.getItem('redirect-route');
    if (route) this.router.navigate([route]);
    else if (this.router.url == '/login') this.router.navigate(['home']);
  }

  disableTitleTooltip() {
    // Remove native title tooltips globally, but allow opt-in via data-allow-native-title
    const onHover = (e: MouseEvent) => {
      const el = (e.target as Element)?.closest<HTMLElement>('[title]');
      if (!el || el.hasAttribute('data-allow-native-title')) return;

      const val = el.getAttribute('title');
      if (!val) return;

      // If using Bootstrap tooltips, preserve content in data-bs-title
      if (el.getAttribute('data-bs-toggle') === 'tooltip') {
        el.setAttribute('data-bs-title', val);
      } else {
        // Or just stash it if you want to read it later
        el.setAttribute('data-title', val);
      }
      el.removeAttribute('title');
    };

    document.addEventListener('mouseover', onHover, { passive: true });
  }
}
